import React, { useCallback, useEffect, useState } from 'react'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { LocationIcon } from 'assets/icons/LocationIcon'
import { WarningTriangleIcon } from 'assets/icons/WarningTriangleIcon'
import { Badge } from 'components/Badge/Badge'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { Input } from 'components/Input/Input'
import { ToggleButton } from 'components/Input/ToggleButton'
import { Modal } from 'components/Modal/Modal'
import { NavBar } from 'components/NavBar/NavBar'
import {
  changeLocationStatus,
  createLocationNode,
  getLocationRegistryTree,
  getLocationRoads,
  getUnmappedLocations,
  saveLocationGeometry,
} from 'features/locationRegistry/locationRegistrySlice'
import { findFirstAreaGeoId, buildCreateLocationAttrs, isStatutoryLocation } from 'features/locationRegistry/locationRegistryMappers'
import useModalClose from 'hooks/useModalClose'
import { toast } from 'react-toastify'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { ISelectValue } from 'constants/globalTypes'
import useFocusFirstInput from 'utils/useFocusFirstInput'
import { Button, CursorPointer, Flex, MainHeadline, PageWrapper, Tabs, TabsContainer, colors } from 'styling/GlobalStyles'
import { BodyText, OtherText, ParagraphText, TitleText } from 'styling/Typography'
import {
  ILocationGeos,
  ILocationNode,
  INewLocationForm,
  INewRoadForm,
} from 'types/locationsRegistry.interface'
import {
  AttrRow,
  ChevronSlot,
  DetailDrawer,
  DrawModeBar,
  LegendDot,
  LegendRow,
  MapColumn,
  MapDrawSidebar,
  MapLayout,
  ModuleBindingRow,
  NodeRowInner,
  RegistryFooter,
  SectionLabel,
  TypeTag,
  UnmappedTag,
  WarnBox,
} from './Locations.styled'
import {
  CHILD_TYPES,
  FUEL_KIND_OPTIONS,
  INDOOR_TYPES,
  MATERIAL_OPTIONS,
  STATUS_COLORS,
  SURFACE_OPTIONS,
  TYPE_META,
  areaHa,
  buildParents,
  centroidOf,
  flattenNodes,
  fmtLL,
  getModuleBindings,
  inPoly,
} from './locationsMockData'
import { LocationsRegistryMap } from './LocationsRegistryMap'

const LOCATION_TABS = ['Registry', 'Map & Geofences']

const blankNewLocation: INewLocationForm = {
  parentId: null,
  type: '',
  name: '',
  material: '',
  capacity: '',
  restricted: false,
  speed: '',
  surface: '',
  beds: '',
  fuelCap: '',
  fuelKind: '',
  statutory: false,
}

const blankNewRoad: INewRoadForm = {
  name: '',
  speed: '',
  surface: '',
}

const StatusBadge = ({ status }: { status: string }) => (
  <Badge text={status} red={status === 'EVACUATED' || status === 'Unaccounted'} green={status === 'OPERATIONAL' || status === 'ACTIVE' || status === 'OK' || status === 'Accounted'} blue={status === 'PLANNED' || status === 'MOBILE' || status === 'RESTRICTED' || status === 'Statutory'} orange={status === 'HELD' || status === 'DEPLETED' || status === 'Drill'} />
)

const ChevronRight = () => (
  <svg viewBox='0 0 20 20'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M7.05806 3.30806C6.81398 3.55214 6.81398 3.94786 7.05806 4.19194L12.8661 10L7.05806 15.8081C6.81398 16.0521 6.81398 16.4479 7.05806 16.6919C7.30214 16.936 7.69786 16.936 7.94194 16.6919L14.1919 10.4419C14.436 10.1979 14.436 9.80214 14.1919 9.55806L7.94194 3.30806C7.69786 3.06398 7.30214 3.06398 7.05806 3.30806Z'
    />
  </svg>
)

const ChevronDown = () => (
  <svg viewBox='0 0 20 20' style={{ transform: 'rotate(90deg)' }}>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M7.05806 3.30806C6.81398 3.55214 6.81398 3.94786 7.05806 4.19194L12.8661 10L7.05806 15.8081C6.81398 16.0521 6.81398 16.4479 7.05806 16.6919C7.30214 16.936 7.69786 16.936 7.94194 16.6919L14.1919 10.4419C14.436 10.1979 14.436 9.80214 14.1919 9.55806L7.94194 3.30806C7.69786 3.06398 7.30214 3.06398 7.05806 3.30806Z'
    />
  </svg>
)

interface INodeRowProps {
  node: ILocationNode
  depth: number
  expanded: { [id: number]: boolean }
  toggle: (id: number) => void
  select: (node: ILocationNode) => void
  selected: ILocationNode | null
  geos: ILocationGeos
}

const NodeRow = ({ node, depth, expanded, toggle, select, selected, geos }: INodeRowProps) => {
  const meta = TYPE_META[node.type] || TYPE_META.AREA
  const hasKids = node.children && node.children.length > 0

  return (
    <>
      <NodeRowInner
        $depth={depth}
        $selected={selected?.id === node.id}
        onClick={() => {
          select(node)
          if (hasKids) toggle(node.id)
        }}
      >
        <ChevronSlot>
          {hasKids ? expanded[node.id] ? <ChevronDown /> : <ChevronRight /> : null}
        </ChevronSlot>
        <span style={{ flexShrink: 0, display: 'flex' }}>{LocationIcon}</span>
        <BodyText weight={node.type === 'SITE' || node.type === 'AREA' ? '700' : '500'}>{node.name}</BodyText>
        <StatusBadge status={node.status} />
        <ParagraphText grey>{meta.label}</ParagraphText>
        {node.restricted && <StatusBadge status='RESTRICTED' />}
        {isStatutoryLocation(node.attrs) && <StatusBadge status='Statutory' />}
        {node.isUnmapped && node.type !== 'SITE' && node.type !== 'AREA' && !INDOOR_TYPES.includes(node.type) && (
          <UnmappedTag>UNMAPPED</UnmappedTag>
        )}
        {node.mounted && <OtherText grey>· mounted: {node.mounted}</OtherText>}
      </NodeRowInner>
      {hasKids &&
        expanded[node.id] &&
        node.children.map((child) => (
          <NodeRow
            key={child.id}
            node={child}
            depth={depth + 1}
            expanded={expanded}
            toggle={toggle}
            select={select}
            selected={selected}
            geos={geos}
          />
        ))}
    </>
  )
}

interface IAddLocationModalProps {
  closeModal: () => void
  tree: ILocationNode
  setExpanded: React.Dispatch<React.SetStateAction<{ [id: number]: boolean }>>
  onCreated: (node: ILocationNode, parent: ILocationNode | null, draw: boolean) => void
}

const AddLocationModal = ({ closeModal, tree, setExpanded, onCreated }: IAddLocationModalProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const allNodes = flattenNodes(tree).filter((n) => n.id > 0)
  const parents = allNodes.filter((n) => (CHILD_TYPES[n.type] || []).length > 0)
  const [form, setForm] = useState<INewLocationForm>(blankNewLocation)

  useFocusFirstInput()
  useModalClose(closeModal)

  const parent = form.parentId != null ? allNodes.find((n) => n.id === form.parentId) : undefined
  const allowedTypes = parent ? CHILD_TYPES[parent.type] || [] : ['SITE']

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSelect = (value: ISelectValue, name: string) => {
    if (name === 'parentId') {
      if (value?.value == null) {
        setForm((prev) => ({ ...prev, parentId: null, type: '' }))
        return
      }
      const p = allNodes.find((n) => n.id === value.value)
      if (!p) return
      setForm((prev) => ({ ...prev, parentId: p.id, type: '' }))
      return
    }
    setForm((prev) => ({ ...prev, [name]: value.value }))
  }

  const canCreate = Boolean(form.type && form.name.trim())

  const parentOptions = parents.map((p) => ({
    label: `${p.name} (${TYPE_META[p.type]?.label})`,
    value: p.id,
  }))

  const typeOptions = allowedTypes.map((t) => ({
    label: TYPE_META[t].label,
    value: t,
  }))

  const materialOptions = MATERIAL_OPTIONS.map((m) => ({ label: m, value: m }))
  const surfaceOptions = SURFACE_OPTIONS.map((x) => ({ label: x, value: x }))
  const fuelKindOptions = FUEL_KIND_OPTIONS.map((x) => ({ label: x, value: x }))

  const handleCreate = async (draw: boolean) => {
    if (!canCreate) return
    const result = await dispatch(
      createLocationNode({
        parentId: form.parentId,
        type: form.type,
        name: form.name.trim(),
        restricted: form.restricted,
        attrs: buildCreateLocationAttrs(form.type, form),
      }),
    )
    if (!createLocationNode.fulfilled.match(result)) return
    if (parent) setExpanded((prev) => ({ ...prev, [parent.id]: true }))
    closeModal()
    onCreated(result.payload, parent ?? null, draw)
  }

  return (
    <Modal black>
      <Flex column between center>
        <Flex column gap='1rem' width='100%'>
          <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
            <TitleText weight='bold'>Add Location</TitleText>
            <CursorPointer onClick={closeModal}>
              <CloseIcon />
            </CursorPointer>
          </Flex>
          <Flex gap='1rem'>
            <HonestSelectInput
              nomarg
              name='parentId'
              labelText='Parent location (optional)'
              options={parentOptions}
              placeholder='Select parent location'
              experimentalResetValue
              defaultValue={form.parentId}
              onChange={handleSelect}
            />
            <HonestSelectInput
              nomarg
              name='type'
              labelText={parent ? `Type (allowed under ${TYPE_META[parent.type]?.label})` : 'Type'}
              options={typeOptions}
              placeholder='Select type'
              experimentalResetValue
              defaultValue={form.type || undefined}
              onChange={handleSelect}
            />
          </Flex>
          <Input
            nomarg
            name='name'
            labelText='Name'
            placeholder={
              form.type === 'STOCKPILE' ? 'e.g. Low Grade SP-3' : form.type === 'ROAD' ? 'e.g. Haul Road H2' : 'Name'
            }
            value={form.name}
            onChange={handleChange}
          />
          {['STOCKPILE', 'DUMP'].includes(form.type) && (
            <>
              <HonestSelectInput
                nomarg
                name='material'
                labelText='Material'
                options={materialOptions}
                placeholder='Select material'
                {...(form.material ? { defaultValue: form.material } : {})}
                onChange={handleSelect}
              />
              <OtherText grey>
                Created PLANNED — lifecycle PLANNED → ACTIVE → DEPLETED → REHABILITATED. Balance starts at zero;
                movements build it.
              </OtherText>
            </>
          )}
          {form.type === 'MUSTER' && (
            <Input
              nomarg
              name='capacity'
              labelText='Capacity (people)'
              type='number'
              value={form.capacity}
              onChange={handleChange}
            />
          )}
          {form.type === 'ROAD' && (
            <Flex gap='1rem'>
              <Input
                nomarg
                name='speed'
                labelText='Typical speed (km/h)'
                type='number'
                value={form.speed}
                onChange={handleChange}
              />
              <HonestSelectInput
                nomarg
                name='surface'
                labelText='Surface'
                options={surfaceOptions}
                placeholder='Select surface'
                {...(form.surface ? { defaultValue: form.surface } : {})}
                onChange={handleSelect}
              />
            </Flex>
          )}
          {form.type === 'ROOM' && (
            <>
              <Input
                nomarg
                name='beds'
                labelText='Beds'
                type='number'
                value={form.beds}
                onChange={handleChange}
              />
              <OtherText grey>
                Bed-level assignment stays in Accommodation — the registry only gives the room an address.
              </OtherText>
            </>
          )}
          {form.type === 'FUEL_STORAGE' && (
            <Flex gap='1rem'>
              <Input
                nomarg
                name='fuelCap'
                labelText='Capacity (L)'
                type='number'
                value={form.fuelCap}
                onChange={handleChange}
              />
              <HonestSelectInput
                nomarg
                name='fuelKind'
                labelText='Kind'
                options={fuelKindOptions}
                placeholder='Select kind'
                {...(form.fuelKind ? { defaultValue: form.fuelKind } : {})}
                onChange={handleSelect}
              />
            </Flex>
          )}
          {form.type === 'TSF' && (
            <ToggleButton
              start
              label='Statutory daily inspection (creates SCHEDULED trigger in Operations)'
              state={form.statutory}
              handleChange={() => setForm({ ...form, statutory: !form.statutory })}
            />
          )}
          <ToggleButton
            start
            label='Restricted — node is not accessible to general operators'
            state={form.restricted}
            handleChange={() => setForm({ ...form, restricted: !form.restricted })}
          />
          {['ZONE', 'SECTION', 'BLOCK', 'PIT'].includes(form.type) && (
            <OtherText grey>
              Mining node — the registry creates the address; geological attributes, status authority and permit gates
              are completed and owned in Operations.
            </OtherText>
          )}
        </Flex>
        {INDOOR_TYPES.includes(form.type) ? (
          <Button mt='16' width100 orange disabled={!canCreate} onClick={() => handleCreate(false)}>
            Create Location
          </Button>
        ) : (
          <Flex gap='1rem' width='100%' mt='16'>
            <Button width100 orangeBorder disabled={!canCreate} onClick={() => handleCreate(false)}>
              Create as Unmapped
            </Button>
            <Button width100 orange disabled={!canCreate} onClick={() => handleCreate(true)}>
              Create & Draw Geofence
            </Button>
          </Flex>
        )}
      </Flex>
    </Modal>
  )
}

interface IAddRoadModalProps {
  closeModal: () => void
  onCreated: (roadId: number, roadName: string) => void
}

const AddRoadModal = ({ closeModal, onCreated }: IAddRoadModalProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const [form, setForm] = useState<INewRoadForm>(blankNewRoad)
  useFocusFirstInput()
  useModalClose(closeModal)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSelect = (value: ISelectValue) => {
    setForm({ ...form, surface: String(value.value) })
  }

  const surfaceOptions = SURFACE_OPTIONS.map((x) => ({ label: x, value: x }))

  const handleCreate = async () => {
    if (!form.name.trim() || !(Number(form.speed) > 0)) return
    const result = await dispatch(
      createLocationNode({
        parentId: null,
        type: 'ROAD',
        name: form.name.trim(),
        restricted: false,
        attrs: buildCreateLocationAttrs('ROAD', form),
      }),
    )
    if (!createLocationNode.fulfilled.match(result)) return
    closeModal()
    onCreated(result.payload.id, result.payload.name)
  }

  return (
    <Modal black>
      <Flex column between center>
        <Flex column gap='1rem' width='100%'>
          <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
            <TitleText weight='bold'>Add Road</TitleText>
            <CursorPointer onClick={closeModal}>
              <CloseIcon />
            </CursorPointer>
          </Flex>
          <Input
            nomarg
            name='name'
            labelText='Road name'
            placeholder='e.g. Haul Road H2 — Pit P3 to Dump North'
            value={form.name}
            onChange={handleChange}
          />
          <Flex gap='1rem'>
            <Input
              nomarg
              name='speed'
              labelText='Typical speed (km/h)'
              type='number'
              value={form.speed}
              onChange={handleChange}
            />
            <HonestSelectInput
              nomarg
              name='surface'
              labelText='Surface'
              options={surfaceOptions}
              defaultValue={form.surface}
              onChange={handleSelect}
            />
          </Flex>
          <OtherText grey>
            Creates a ROAD node under Registry → Roads / Access, then drops you straight into drawing mode.
          </OtherText>
        </Flex>
        <Button mt='16' width100 orange disabled={!form.name.trim() || !(Number(form.speed) > 0)} onClick={handleCreate}>
          Create & Draw
        </Button>
      </Flex>
    </Modal>
  )
}

const STATUS_OPTIONS = ['OPERATIONAL', 'HELD', 'EVACUATED'].map((s) => ({ label: s, value: s }))
const HOLD_CATEGORY_OPTIONS = ['GROUND', 'WEATHER', 'EQUIPMENT', 'SAFETY', 'OTHER'].map((c) => ({
  label: c,
  value: c,
}))

interface IChangeStatusModalProps {
  closeModal: () => void
  node: ILocationNode
}

const ChangeStatusModal = ({ closeModal, node }: IChangeStatusModalProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const [status, setStatus] = useState(node.status === 'HELD' || node.status === 'EVACUATED' ? node.status : 'OPERATIONAL')
  const [reason, setReason] = useState('')
  const [category, setCategory] = useState('GROUND')

  useFocusFirstInput()
  useModalClose(closeModal)

  const handleSelect = (value: ISelectValue, name: string) => {
    if (name === 'status') setStatus(String(value.value))
    if (name === 'category') setCategory(String(value.value))
  }

  const handleSubmit = async () => {
    if (!reason.trim()) return
    const result = await dispatch(
      changeLocationStatus({
        id: node.id,
        status,
        reason: reason.trim(),
        category: status === 'HELD' ? category : undefined,
      }),
    )
    if (changeLocationStatus.fulfilled.match(result)) {
      closeModal()
    }
  }

  return (
    <Modal black>
      <Flex column between center>
        <Flex column gap='1rem' width='100%'>
          <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
            <TitleText weight='bold'>Change Status — {node.name}</TitleText>
            <CursorPointer onClick={closeModal}>
              <CloseIcon />
            </CursorPointer>
          </Flex>
          <HonestSelectInput
            nomarg
            name='status'
            labelText='Status'
            options={STATUS_OPTIONS}
            defaultValue={status}
            onChange={handleSelect}
          />
          {status === 'HELD' && (
            <HonestSelectInput
              nomarg
              name='category'
              labelText='Hold category'
              options={HOLD_CATEGORY_OPTIONS}
              defaultValue={category}
              onChange={handleSelect}
            />
          )}
          <Input
            nomarg
            name='reason'
            labelText='Reason'
            placeholder='Mandatory reason for this status change'
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Flex>
        <Button mt='16' width100 orange disabled={!reason.trim()} onClick={handleSubmit}>
          Save Status
        </Button>
      </Flex>
    </Modal>
  )
}

interface IRegistryTabProps {
  tree: ILocationNode
  geos: ILocationGeos
  openDraw: (nodeId: number) => void
  say: (msg: string) => void
}

const RegistryTab = ({ tree, geos, openDraw, say }: IRegistryTabProps) => {
  const [expanded, setExpanded] = useState([])
  const [selected, setSelected] = useState<ILocationNode | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [statusOpen, setStatusOpen] = useState(false)

  useEffect(() => {
    if (!selected) return
    const updated = flattenNodes(tree).find((n) => n.id === selected.id)
    if (updated) setSelected(updated)
  }, [tree])

  const toggle = (id: number) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))

  const handleCreated = (newNode: ILocationNode, parent: ILocationNode | null, draw: boolean) => {
    const parentLabel = parent ? parent.name : 'the registry'
    if (INDOOR_TYPES.includes(newNode.type)) {
      say(
        `${TYPE_META[newNode.type].label} "${newNode.name}" created under ${parentLabel} — indoor node, geometry optional`,
      )
    } else if (draw) {
      say(
        `${TYPE_META[newNode.type].label} "${newNode.name}" created under ${parentLabel} — now capture its geometry`,
      )
      openDraw(newNode.id)
    } else {
      say(
        `${TYPE_META[newNode.type].label} "${newNode.name}" created UNMAPPED — flagged in the registry until geometry is captured`,
      )
    }
  }

  const bindings = selected ? getModuleBindings(selected) : []

  return (
    <>
      <Container>
        <ContainerHeader padding>
          <Flex between width='100%' center>
            <TitleText weight='bold'>
              Location Registry — one typed tree, existing module trees mounted unchanged
            </TitleText>
            <Button orange onClick={() => setAddOpen(true)}>
              Add Location
            </Button>
          </Flex>
        </ContainerHeader>
        <ContainerContent>
          <NodeRow
            node={tree}
            depth={0}
            expanded={expanded}
            toggle={toggle}
            select={setSelected}
            selected={selected}
            geos={geos}
          />
          <RegistryFooter>
            <OtherText grey>
              Status is universal (any node can be HELD / EVACUATED) — gating stays domain-scoped: permit gates check
              mining nodes, check-in checks camp nodes.
            </OtherText>
          </RegistryFooter>
        </ContainerContent>
      </Container>

      {selected && (
        <DetailDrawer>
          <Flex between width='100%'>
            <TitleText weight='bold'>{selected.name}</TitleText>
            <CursorPointer onClick={() => setSelected(null)}>
              <CloseIcon />
            </CursorPointer>
          </Flex>
          <Flex gap='0.5rem' style={{ margin: '0.625rem 0' }}>
            <StatusBadge status={selected.status} />
            <TypeTag>{TYPE_META[selected.type]?.label}</TypeTag>
          </Flex>
          {selected.attrs && Object.keys(selected.attrs).length > 0 && (
            <>
              <SectionLabel>Type-specific attributes</SectionLabel>
              {Object.entries(selected.attrs).map(([key, value]) => (
                <AttrRow key={key}>
                  <BodyText grey>{key}</BodyText>
                  <BodyText style={{ textAlign: 'right', maxWidth: '15rem' }}>{String(value)}</BodyText>
                </AttrRow>
              ))}
            </>
          )}
          {selected.type === 'PIT' && selected.status === 'HELD' && (
            <WarnBox>
              {WarningTriangleIcon}
              <OtherText color={colors.orange}>
                Plans scoped here are skipped by the scheduler; permits refused — Operations gates unchanged by the
                registry.
              </OtherText>
            </WarnBox>
          )}
          {['ZONE', 'SECTION', 'BLOCK', 'PIT'].includes(selected.type) && (
            <OtherText grey style={{ marginTop: '0.625rem' }}>
              Mining node — geology, status authority ({String(selected.attrs?.authority || 'per Ops role table')}) and
              propagation rules owned by Operations. The registry only gives it a universal address.
            </OtherText>
          )}
          <SectionLabel>Module bindings at this node</SectionLabel>
          {bindings.map(([module, detail]) => (
            <ModuleBindingRow key={module}>
              <BodyText weight='600' style={{ width: '5.625rem' }}>
                {module}
              </BodyText>
              <BodyText grey>{detail}</BodyText>
            </ModuleBindingRow>
          ))}
          <SectionLabel>Geometry</SectionLabel>
          {geos[selected.id] ? (
            <BodyText grey>
              {geos[selected.id].kind === 'polygon'
                ? `Polygon · ${geos[selected.id].pts.length} vertices · ${areaHa(geos[selected.id].pts).toFixed(1)} ha`
                : geos[selected.id].kind === 'polyline'
                  ? `Polyline · ${geos[selected.id].pts.length} vertices`
                  : 'Point'}{' '}
              · centroid {fmtLL(centroidOf(geos[selected.id].pts) as [number, number])}
            </BodyText>
          ) : (
            <BodyText grey>No geofence captured yet.</BodyText>
          )}
          <Flex gap='0.5rem' style={{ marginTop: '0.875rem' }}>
            <Button orangeBorder onClick={() => setStatusOpen(true)}>
              Change Status
            </Button>
            <Button orangeBorder onClick={() => openDraw(selected.id)}>
              {geos[selected.id] ? 'Edit Geofence' : 'Draw Geofence'}
            </Button>
          </Flex>
        </DetailDrawer>
      )}

      {addOpen && (
        <AddLocationModal
          closeModal={() => setAddOpen(false)}
          tree={tree}
          setExpanded={setExpanded}
          onCreated={handleCreated}
        />
      )}

      {statusOpen && selected && (
        <ChangeStatusModal closeModal={() => setStatusOpen(false)} node={selected} />
      )}
    </>
  )
}

interface IMapTabProps {
  tree: ILocationNode
  geos: ILocationGeos
  drawTarget: number | null
  setDrawTarget: React.Dispatch<React.SetStateAction<number | null>>
  say: (msg: string) => void
}

const MapGeofencesTab = ({ tree, geos, drawTarget, setDrawTarget, say }: IMapTabProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const nodes = flattenNodes(tree)
  const nodeById = (id: number) => nodes.find((n) => n.id === id)
  const [draft, setDraft] = useState<number[][]>([])
  const [hoverLL, setHoverLL] = useState<[number, number] | null>(null)
  const [coordLat, setCoordLat] = useState('')
  const [coordLng, setCoordLng] = useState('')
  const [roadOpen, setRoadOpen] = useState(false)
  const [hoveredGeoId, setHoveredGeoId] = useState<number | null>(null)
  const [selectedMapId, setSelectedMapId] = useState<number | null>(null)

  useEffect(() => {
    if (drawTarget) {
      setSelectedMapId(null)
      setHoveredGeoId(null)
    }
    if (drawTarget && geos[drawTarget]) {
      setDraft(geos[drawTarget].pts)
    } else {
      setDraft([])
    }
  }, [drawTarget, geos])

  const parentMap = buildParents(tree)
  const target = drawTarget ? nodeById(drawTarget) : null

  const ancestorPoly = (id: number) => {
    let parentId = parentMap[id]
    while (parentId) {
      const geo = geos[parentId]
      if (geo && geo.kind === 'polygon') {
        return { id: parentId, name: nodeById(parentId)?.name, pts: geo.pts }
      }
      parentId = parentMap[parentId]
    }
    return null
  }

  const ancestor = drawTarget ? ancestorPoly(drawTarget) : null
  const outsideCount = ancestor ? draft.filter((p) => !inPoly(p, ancestor.pts)).length : 0
  const geoIds = Object.keys(geos).map(Number)
  const focusAreaId = findFirstAreaGeoId(tree, geos)

  const geofenceOptions = nodes
    .filter((n) => n.type !== 'AREA')
    .map((n) => ({ label: n.name, value: n.id }))

  const handleGeofenceSelect = useCallback((value: ISelectValue) => {
    if (value?.value) setDrawTarget(Number(value.value))
  }, [])

  const handleMapClick = useCallback(
    (lat: number, lng: number) => {
      if (!drawTarget) return
      setDraft((prev) => [...prev, [lat, lng]])
    },
    [drawTarget],
  )

  const handleMouseMove = useCallback((coords: [number, number] | null) => {
    setHoverLL(coords)
  }, [])

  const handleGeoHover = useCallback((id: number | null) => {
    setHoveredGeoId(id)
  }, [])

  const handleGeoClick = useCallback((id: number | null) => {
    setSelectedMapId(id)
  }, [])

  const hoveredNode = hoveredGeoId ? nodeById(hoveredGeoId) : null
  const selectedMapNode = selectedMapId ? nodeById(selectedMapId) : null

  const saveDraft = async () => {
    if (!drawTarget || !target) return
    if (ancestor && outsideCount > 0) {
      say(
        `Cannot save: ${outsideCount} vertex(es) outside ${ancestor.name} — geometry must stay within parent area.`,
      )
      return
    }
    const result = await dispatch(
      saveLocationGeometry({
        id: drawTarget,
        draft,
        nodeType: target.type,
      }),
    )
    if (!saveLocationGeometry.fulfilled.match(result)) return
    setDraft([])
    setDrawTarget(null)
  }

  const colorFor = (node: ILocationNode | undefined, geo: { restricted?: boolean }) => {
    if (node?.restricted || geo.restricted) return '#A78BFA'
    if (node?.status === 'HELD') return colors.orange
    if (node?.status === 'EVACUATED') return colors.red
    if (['STOCKPILE', 'DUMP'].includes(node?.type || '')) return colors.blue
    return colors.green
  }

  return (
    <>
      <Container>
        <ContainerHeader padding>
          <Flex between width='100%' center wrap gap='0.5rem'>
            <TitleText weight='bold'>Site Map — geofences on registry nodes</TitleText>
            <Flex gap='0.5rem' center wrap>
              {!drawTarget && hoveredNode && (
                <OtherText grey>
                  {hoveredNode.name} · {TYPE_META[hoveredNode.type]?.label}
                </OtherText>
              )}
              {!drawTarget && selectedMapNode && (
                <Flex gap='0.5rem' center>
                  <BodyText weight='600'>{selectedMapNode.name}</BodyText>
                  <Button orangeBorder onClick={() => setDrawTarget(selectedMapId)}>
                    Edit Geofence
                  </Button>
                  <CursorPointer onClick={() => setSelectedMapId(null)}>
                    <CloseIcon />
                  </CursorPointer>
                </Flex>
              )}
              {drawTarget && hoverLL && <OtherText grey>cursor: {fmtLL(hoverLL)}</OtherText>}
              {!drawTarget && (
                <HonestSelectInput
                  nomarg
                  name='geofence'
                  width='15rem'
                  placeholder='Draw / edit geofence for…'
                  options={geofenceOptions}
                  onChange={handleGeofenceSelect}
                />
              )}
              {!drawTarget && (
                <Button orange onClick={() => setRoadOpen(true)}>
                  Add Road
                </Button>
              )}
            </Flex>
          </Flex>
        </ContainerHeader>
        <ContainerContent>
          <MapLayout>
            <MapColumn>
              <LocationsRegistryMap
                geos={geos}
                geoIds={geoIds}
                nodeById={nodeById}
                drawTarget={drawTarget}
                draft={draft}
                ancestor={ancestor}
                outsideCount={outsideCount}
                colorFor={colorFor}
                onMapClick={handleMapClick}
                onMouseMove={handleMouseMove}
                onGeoHover={handleGeoHover}
                onGeoClick={handleGeoClick}
                selectedGeoId={selectedMapId}
                focusAreaId={focusAreaId}
              />
              <LegendRow>
                <OtherText grey>
                  <LegendDot $color={colors.green}>■</LegendDot> operational
                </OtherText>
                <OtherText grey>
                  <LegendDot $color={colors.orange}>■</LegendDot> held
                </OtherText>
                <OtherText grey>
                  <LegendDot $color={colors.blue}>■</LegendDot> stockpile/dump
                </OtherText>
                <OtherText grey>
                  <LegendDot $color='#A78BFA'>▨</LegendDot> restricted
                </OtherText>
                <OtherText grey>
                  <LegendDot $color={colors.orange}>●</LegendDot> muster point
                </OtherText>
              </LegendRow>
            </MapColumn>
            {drawTarget && (
              <MapDrawSidebar>
                <TitleText weight='bold'>Survey coordinates</TitleText>
                <Input
                  nomarg
                  labelText='Latitude'
                  placeholder='Lat (6.68xxx)'
                  value={coordLat}
                  onChange={(e) => setCoordLat(e.target.value)}
                />
                <Input
                  nomarg
                  labelText='Longitude'
                  placeholder='Lng (−1.62xxx)'
                  value={coordLng}
                  onChange={(e) => setCoordLng(e.target.value)}
                />
                <Button
                  width100
                  orangeBorder
                  disabled={!(parseFloat(coordLat) && parseFloat(coordLng))}
                  onClick={() => {
                    setDraft([...draft, [parseFloat(coordLat), parseFloat(coordLng)]])
                    setCoordLat('')
                    setCoordLng('')
                  }}
                >
                  Add vertex
                </Button>
              </MapDrawSidebar>
            )}
          </MapLayout>
          {drawTarget && (
            <DrawModeBar>
              <Flex between width='100%' center gap='0.5rem' wrap>
                <Flex column gap='0.25rem'>
                  <TitleText weight='bold'>Drawing geofence for {target?.name}</TitleText>
                  <OtherText grey>
                    Click the map to add vertices ({draft.length} placed).
                    {ancestor &&
                      ` Must stay inside ${ancestor.name}${outsideCount > 0 ? ` — ${outsideCount} vertex(es) outside` : ''}.`}
                  </OtherText>
                </Flex>
                <Flex gap='0.5rem'>
                  <Button
                    onClick={() => {
                      setDraft([])
                      setDrawTarget(null)
                    }}
                  >
                    Cancel
                  </Button>
                  <Button disabled={draft.length === 0} onClick={() => setDraft(draft.slice(0, -1))}>
                    Undo
                  </Button>
                  <Button orange disabled={draft.length === 0 || outsideCount > 0} onClick={saveDraft}>
                    Save Geofence
                  </Button>
                </Flex>
              </Flex>
            </DrawModeBar>
          )}
        </ContainerContent>
      </Container>

      {roadOpen && (
        <AddRoadModal
          closeModal={() => setRoadOpen(false)}
          onCreated={(roadId, roadName) => {
            setDrawTarget(roadId)
            say(`Road "${roadName}" created — now draw its polyline`)
          }}
        />
      )}
    </>
  )
}

export const Locations = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { tree, geos } = useSelector((state: RootState) => state.locationRegistry)
  const [activeTab, setActiveTab] = useState('Registry')
  const [drawTarget, setDrawTarget] = useState<number | null>(null)

  useEffect(() => {
    dispatch(getLocationRegistryTree())
    dispatch(getUnmappedLocations())
    dispatch(getLocationRoads())
  }, [])

  const say = (msg: string) => toast.info(msg)

  const openDraw = (nodeId: number) => {
    setDrawTarget(nodeId)
    setActiveTab('Map & Geofences')
  }

  if (!tree) {
    return (
      <>
        <NavBar />
        <PageWrapper noFooter>
          <Flex between center mb='16'>
            <MainHeadline>Locations</MainHeadline>
          </Flex>
        </PageWrapper>
      </>
    )
  }

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <Flex between center mb='16'>
          <MainHeadline>Locations</MainHeadline>
        </Flex>
        <Flex mb='16'>
          <TabsContainer>
            {LOCATION_TABS.map((tab) => (
              <Tabs key={tab} active={activeTab === tab} onClick={() => setActiveTab(tab)}>
                {tab}
              </Tabs>
            ))}
          </TabsContainer>
        </Flex>
        {activeTab === 'Registry' && (
          <RegistryTab tree={tree} geos={geos} openDraw={openDraw} say={say} />
        )}
        {activeTab === 'Map & Geofences' && (
          <MapGeofencesTab
            tree={tree}
            geos={geos}
            drawTarget={drawTarget}
            setDrawTarget={setDrawTarget}
            say={say}
          />
        )}
      </PageWrapper>
    </>
  )
}
